// Admin: user invitations.
//
// Same idea as the print-agent token flow — the raw token is shown
// EXACTLY ONCE on create.  Admin copies the URL (or hits "mailto:") and
// after that the DB only stores sha256(token), so a re-display is
// impossible.  If lost — revoke and create a new one.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { request } from "@/lib/api";
import { useSystemSettings } from "@/lib/system";
import { formatDateTime } from "@/lib/format";
import { useRoles } from "@/lib/roles";

interface InvitationRead {
  id: string;
  email: string | null;
  role: string;
  allowed_stage_ids: number[];
  allowed_device_ids: string[];
  invited_by: string | null;
  inviter: { id: string; name: string; login: string | null } | null;
  invited_at: string;
  expires_at: string;
  accepted_at: string | null;
  accepted_by: string | null;
  revoked_at: string | null;
  status: "pending" | "accepted" | "revoked" | "expired";
}

interface CreateResponse extends InvitationRead {
  raw_token: string;
  accept_url: string;
  email_sent?: boolean;
  email_skipped?: boolean;
  email_error?: string | null;
}

const STATUS_CLS: Record<InvitationRead["status"], string> = {
  pending: "pill-warn",
  accepted: "pill-ok",
  revoked: "",
  expired: "",
};

export default function InvitationsList() {
  return (
    <RequirePermission codes="user.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [createdInvite, setCreatedInvite] = useState<CreateResponse | null>(null);

  const list = useQuery({
    queryKey: ["admin", "invitations"],
    queryFn: () =>
      request<{ items: InvitationRead[]; pagination: { total: number } }>(
        "/admin/invitations?page_size=100",
      ),
  });

  // System settings — for the corporate-domain UX in the create modal.
  const settings = useQuery({
    queryKey: ["system", "settings"],
    queryFn: () => request<{ corporate_domain: string | null }>("/system/settings"),
    staleTime: 60_000,
  });

  const revoke = useMutation({
    mutationFn: (id: string) =>
      request<InvitationRead>(`/admin/invitations/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "invitations"] }),
  });

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminPeopleAccess")}
        title={t("invitations.title")}
        description={t("invitations.description")}
        actions={
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            {t("invitations.invite")}
          </button>
        }
      />

      {list.isLoading && <LoadingState />}
      {list.isError && <ErrorState error={list.error} />}

      {list.data && list.data.items.length === 0 && (
        <div className="card p-8 mt-4 text-center text-text-muted">
          {t("invitations.empty")}
        </div>
      )}

      {list.data && list.data.items.length > 0 && (
        <section className="card mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt text-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">{t("invitations.colEmail")}</th>
                <th className="px-3 py-2 text-left">{t("invitations.colRole")}</th>
                <th className="px-3 py-2 text-left">{t("invitations.colCreated")}</th>
                <th className="px-3 py-2 text-left">{t("invitations.colInvitedBy")}</th>
                <th className="px-3 py-2 text-left">{t("invitations.colExpires")}</th>
                <th className="px-3 py-2 text-left">{t("invitations.colStatus")}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((inv) => (
                <tr key={inv.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs">
                    {inv.email ?? <span className="text-text-muted">— (link only)</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className="pill pill-accent text-xs">{inv.role}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-text-muted font-mono">
                    {formatDateTime(inv.invited_at)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {inv.inviter ? (
                      <span title={inv.inviter.login ?? undefined}>
                        {inv.inviter.name || inv.inviter.login}
                      </span>
                    ) : (
                      <span className="text-text-muted">{t("invitations.systemInviter")}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-text-muted font-mono">
                    {formatDateTime(inv.expires_at)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`pill ${STATUS_CLS[inv.status]} text-xs`}>
                      {t(`invitations.statusLabels.${inv.status}`)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {inv.status === "pending" && (
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          if (confirm(t("invitations.confirmRevoke"))) revoke.mutate(inv.id);
                        }}
                        disabled={revoke.isPending}
                      >
                        {t("invitations.revoke")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {revoke.isError && (
        <div className="mt-3">
          <ErrorBanner onDismiss={() => revoke.reset()}>
            {(revoke.error as Error).message}
          </ErrorBanner>
        </div>
      )}

      {showCreate && !createdInvite && (
        <CreateModal
          corporateDomain={settings.data?.corporate_domain ?? null}
          onClose={() => setShowCreate(false)}
          onCreated={(c) => {
            setShowCreate(false);
            setCreatedInvite(c);
            qc.invalidateQueries({ queryKey: ["admin", "invitations"] });
          }}
        />
      )}

      {createdInvite && (
        <TokenModal invite={createdInvite} onClose={() => setCreatedInvite(null)} />
      )}
    </>
  );
}

// ---------- Create dialog -------------------------------------------------

function CreateModal({
  onClose,
  onCreated,
  corporateDomain,
}: {
  onClose: () => void;
  onCreated: (r: CreateResponse) => void;
  corporateDomain: string | null;
}) {
  const { t } = useTranslation();
  const [emailLocal, setEmailLocal] = useState("");
  const [emailFull, setEmailFull] = useState("");
  const [role, setRole] = useState<string>("OPERATOR");
  const roles = useRoles();

  // In corporate mode operator types only the local part; @domain is
  // auto-appended.  In free mode they type the whole address.
  const email = corporateDomain ? `${emailLocal}@${corporateDomain}` : emailFull;

  const m = useMutation({
    mutationFn: () =>
      request<CreateResponse>("/admin/invitations", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim() || null,
          role,
        }),
      }),
    onSuccess: onCreated,
  });

  const canSubmit =
    corporateDomain
      ? /^[^@\s]+$/.test(emailLocal.trim())
      : /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailFull.trim());

  return (
    <div className="fixed inset-0 z-30 bg-page/70 backdrop-blur flex items-center justify-center p-6">
      <form
        className="card p-6 w-full max-w-md space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <header className="flex items-center justify-between">
          <h2 className="text-lg">{t("invitations.modalTitle")}</h2>
          <button type="button" className="btn text-xs" onClick={onClose}>×</button>
        </header>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            {corporateDomain ? t("invitations.emailLabelCorp", { domain: corporateDomain }) : t("invitations.emailLabelFree")}
          </span>
          {corporateDomain ? (
            <div className="mt-1 flex items-stretch rounded-md bg-surface border border-border overflow-hidden focus-within:border-accent">
              <input
                className="flex-1 min-w-0 px-3 py-2 bg-transparent border-0 outline-none font-mono"
                value={emailLocal}
                onChange={(e) => setEmailLocal(e.target.value)}
                placeholder="ivan.petrov"
                required
              />
              <span className="px-3 py-2 font-mono text-text-muted text-sm whitespace-nowrap select-none border-l border-border bg-surface-alt flex items-center">
                @{corporateDomain}
              </span>
            </div>
          ) : (
            <input
              type="email"
              className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border"
              value={emailFull}
              onChange={(e) => setEmailFull(e.target.value)}
              placeholder="user@example.com"
              required
            />
          )}
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            {t("invitations.roleLabel")}
          </span>
          <select
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            {roles.assignable.map((r) => (
              <option key={r.role} value={r.role}>{r.name}</option>
            ))}
          </select>
        </label>
        {m.isError && (
          <ErrorBanner onDismiss={() => m.reset()}>{(m.error as Error).message}</ErrorBanner>
        )}
        <p className="text-text-muted text-xs">
          {t("invitations.linkLifetimeHint")}
        </p>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button type="button" className="btn" onClick={onClose} disabled={m.isPending}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={m.isPending || !canSubmit}>
            {m.isPending ? t("common.creating") : t("common.create")}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- One-time token display ----------------------------------------

function TokenModal({
  invite,
  onClose,
}: {
  invite: CreateResponse;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<"url" | "mailto" | null>(null);
  const company = useSystemSettings().data?.company;
  const mailto = invite.email
    ? `mailto:${encodeURIComponent(invite.email)}?subject=${encodeURIComponent(
        t("invitations.mailSubject"),
      )}&body=${encodeURIComponent(
        t("invitations.mailBody", { url: invite.accept_url, company: company?.name ?? "" }),
      )}`
    : null;

  async function copy(text: string, kind: "url" | "mailto") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      alert(t("invitations.copyFailed") + "\n\n" + text);
    }
  }

  return (
    <div className="fixed inset-0 z-30 bg-page/70 backdrop-blur flex items-center justify-center p-6">
      <div className="card p-6 w-full max-w-lg space-y-4">
        <header className="flex items-center justify-between">
          <h2 className="text-lg">{t("invitations.createdTitle")}</h2>
          <button type="button" className="btn text-xs" onClick={onClose}>×</button>
        </header>
        {invite.email && invite.email_sent && (
          <div className="card p-3 border-accent">
            <p className="text-sm">
              ✉ <strong>{t("invitations.emailSent")}</strong>{" "}
              <span className="font-mono">{invite.email}</span>. {t("invitations.emailSentHint")}
            </p>
          </div>
        )}
        {invite.email && invite.email_sent === false && (
          <div className="card p-3 border-warn">
            <p className="text-sm">
              ⚠ {t("invitations.emailFailed")}{" "}
              <span className="font-mono">{invite.email}</span>
              {invite.email_skipped
                ? ` — ${t("invitations.smtpNotConfigured")}`
                : invite.email_error
                  ? `: ${invite.email_error}`
                  : "."}{" "}
              {t("invitations.emailFailedHint")}
            </p>
          </div>
        )}
        <div className="card p-3 bg-surface-alt border-warn">
          <p className="text-sm">
            {t("invitations.onceWarning1")} <strong>{t("invitations.onceWarningBold")}</strong>. {t("invitations.onceWarning2")}
          </p>
        </div>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            {t("invitations.inviteLink")}
          </span>
          <div className="mt-1 flex gap-2">
            <input
              readOnly
              className="flex-1 px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs"
              value={invite.accept_url}
              onFocus={(e) => e.target.select()}
            />
            <button className="btn" type="button" onClick={() => copy(invite.accept_url, "url")}>
              {copied === "url" ? t("invitations.copied") : t("invitations.copy")}
            </button>
          </div>
        </label>
        {mailto && (
          <div className="flex gap-2">
            <a className="btn btn-primary flex-1 text-center" href={mailto}>
              {t("invitations.openEmail")}
            </a>
            <button className="btn" onClick={() => copy(mailto, "mailto")}>
              {copied === "mailto" ? "✓" : "📋"}
            </button>
          </div>
        )}
        <div className="text-xs text-text-muted">
          {t("invitations.expires")} <span className="font-mono">{formatDateTime(invite.expires_at)}</span> · {t("invitations.roleColon")}{" "}
          <span className="pill pill-accent">{invite.role}</span>
        </div>
        <div className="flex justify-end border-t border-border pt-3">
          <button className="btn btn-primary" onClick={onClose}>
            {t("invitations.done")}
          </button>
        </div>
      </div>
    </div>
  );
}
